import React from 'react';
import clsx from 'clsx';
import formatDate from '@smart-tv/utils/date/format-date';
import { formatPlaytime, getProgressPercentage } from './player-controls-scrubber-utils';
import useSlider from './use-slider';
import styles from './player-controls-scrubber.module.scss';
import Typography from '@smart-tv/components/typography';
import testIds from '@smart-tv/e2e/shared/variables';
import { usePlayerContext } from '../../_contexts/player-context';

export interface SliderClassNames {
  bar?: string;
  track?: string;
  range?: string;
  handle?: string;
}

export interface Props {
  progressPercentage: number;
  rounded?: boolean;
  onChange?: (value: number) => void;
  onChangeCommitted?: (value: number) => void;
  onMouseMove?: (value: number) => void;
  onMouseLeave?: (event: MouseEvent | TouchEvent) => void;
  color?: 'primary' | 'secondary' | 'error' | 'success';
  min?: number;
  max?: number;
  step?: number;
  classNames?: SliderClassNames;
  startTime?: Date;
  endTime?: Date;
  liveProgressPercentage?: number;
}

export const Slider = (props: Props) => {
  const {
    onChange,
    onChangeCommitted,
    onMouseMove,
    onMouseLeave,

    progressPercentage,
    color = 'primary',
    min = 0,
    max = 100,
    step = 1,
    classNames,
    rounded = true,
    startTime,
    endTime,
    liveProgressPercentage,
    ...rest
  } = props;

  const {
    sliderRef,
    sliderTrackRef,
    showMarker,
    progressPercentageValidated,
    progressSkipSeconds,
    isStartover,
    isSliderFocused
  } = useSlider({
    min,
    max,
    step,
    progressPercentage,
    onChange,
    onChangeCommitted,
    onMouseMove,
    onMouseLeave,
    rounded
  });

  const { player } = usePlayerContext();

  const skippingRange = player
    ? getProgressPercentage(player?.getSeekableRange()?.end, player?.getDuration())
    : undefined;
  return (
    <div className={styles['slider']}>
      {startTime && (
        <Typography
          component="div"
          variant="caption"
          data-testid={testIds.player.playerControls.leftTime}
          className={clsx(styles['scrubber-time-label'], styles['scrubber-time-label--starting'])}
        >
          {formatDate(startTime, 'hh:mm')}
        </Typography>
      )}
      <div
        ref={sliderRef}
        tabIndex={0}
        className={clsx(
          styles['slider__bar'],
          styles[`slider__bar--color-${color}`],
          classNames?.bar
        )}
        {...rest}
      >
        <div ref={sliderTrackRef} className={clsx(styles['slider__track'], classNames?.track)} />

        <div
          style={{
            width: `${
              isStartover
                ? liveProgressPercentage
                : skippingRange
                ? skippingRange
                : progressPercentageValidated
            }%`
          }}
          className={clsx(styles['slider__range'], classNames?.range)}
        />

        {showMarker && (
          <Typography
            variant="caption"
            style={{
              left: `${progressPercentageValidated}%`,
              // typography-cascade-workaround: remove fontWeight + lineHeight once Typography uses CSS modules
              fontWeight: 700,
              lineHeight: 'var(--skip-time-marker-line-height)'
            }}
            className={clsx(styles['slider__skip-time-marker'], {
              [styles['slider__skip-time-marker--focused']]: isSliderFocused
            })}
          >
            {formatPlaytime(progressSkipSeconds, player?.getSeekableRange()?.end)}
          </Typography>
        )}

        <div
          role="slider"
          aria-valuemax={max}
          aria-valuemin={min}
          aria-valuenow={progressPercentageValidated}
          style={{ left: `${progressPercentageValidated}%` }}
          className={clsx(styles['slider__handle'], classNames?.handle)}
        />
      </div>
      {endTime && (
        <Typography
          component="div"
          variant="caption"
          data-testid={testIds.player.playerControls.rightTime}
          className={clsx(styles['scrubber-time-label'], styles['scrubber-time-label--ending'])}
        >
          {formatDate(endTime, 'hh:mm')}
        </Typography>
      )}
    </div>
  );
};

export default Slider;
